import { useEffect, useState } from "react";
import { Shield, CheckCircle, Copy, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

export default function Security() {
  const [status, setStatus] = useState({ enabled: false });
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [disablePw, setDisablePw] = useState("");

  useEffect(() => {
    api.get("/auth/2fa/status").then((r) => setStatus(r.data));
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: code.trim() });
      setRecovery(data.recovery_codes);
      setStatus({ enabled: true });
      setSetupData(null);
      setCode("");
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/2fa/disable", { password: disablePw });
      setStatus({ enabled: false });
      setDisablePw("");
      toast.success("Two-factor authentication disabled");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Account · Security"
        title="Two-factor authentication"
        subtitle="Add a second factor to your login. Use Google Authenticator, 1Password, Authy or any RFC 6238 TOTP app."
        guideId="security"
      />

      <section className="px-8 pt-8 max-w-3xl">
        <div className={`border p-6 ${status.enabled ? "border-eco/30 bg-eco/5" : "theme-border theme-surface"}`}>
          <div className="flex items-center gap-3">
            <Shield size={22} weight="fill" className={status.enabled ? "text-eco" : "text-gold"} />
            <div>
              <div className="overline">{status.enabled ? "Enabled" : "Disabled"}</div>
              <h2 className="font-serif-display text-2xl theme-ink mt-1">
                {status.enabled ? "Your account is protected by 2FA" : "Set up 2FA in under a minute"}
              </h2>
            </div>
          </div>

          {!status.enabled && !setupData && (
            <button
              onClick={startSetup}
              disabled={busy}
              data-testid="setup-2fa-btn"
              className="mt-6 bg-gold text-black px-5 py-2.5 text-sm font-medium rounded-sm hover:bg-[#e0bc40] disabled:opacity-50"
            >
              {busy ? "Preparing…" : "Set up authenticator"}
            </button>
          )}

          {!status.enabled && setupData && !recovery && (
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <div>
                <div className="overline mb-2">1 · Scan the QR</div>
                <div className="bg-white p-3 inline-block rounded-sm">
                  <img src={`data:image/png;base64,${setupData.qr_png_base64}`} alt="2FA QR" width={180} height={180} />
                </div>
                <div className="mt-3 text-xs theme-ink-muted">Or enter the secret manually:</div>
                <div className="font-mono-data text-xs theme-ink select-all mt-1 break-all">{setupData.secret}</div>
              </div>
              <form onSubmit={confirmEnable} className="space-y-3">
                <div className="overline">2 · Verify 6-digit code</div>
                <input
                  data-testid="verify-2fa-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  className="w-full theme-input border px-3 py-3 text-2xl font-mono-data text-center tracking-[0.5em] rounded-sm focus:outline-none focus:border-gold"
                  placeholder="123456"
                />
                <button type="submit" disabled={busy} data-testid="verify-2fa-btn"
                  className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50">
                  {busy ? "Verifying…" : "Confirm & enable"}
                </button>
                <button type="button" onClick={() => setSetupData(null)}
                  className="w-full text-xs theme-ink-muted hover:theme-ink">Cancel</button>
              </form>
            </div>
          )}

          {recovery && (
            <div className="mt-6">
              <div className="flex items-center gap-2 text-eco mb-3">
                <CheckCircle size={16} weight="fill" /> 2FA enabled. Store these recovery codes.
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-md">
                {recovery.map((c) => (
                  <div key={c} className="font-mono-data text-sm theme-input border px-3 py-2 select-all">{c}</div>
                ))}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(recovery.join("\n"));
                  toast.success("Copied recovery codes");
                }}
                data-testid="copy-recovery-btn"
                className="mt-3 text-xs flex items-center gap-1 theme-ink-muted hover:text-gold"
              >
                <Copy size={12} /> Copy all
              </button>
              <button onClick={() => setRecovery(null)} className="mt-4 block text-xs theme-ink-muted hover:theme-ink">Done</button>
            </div>
          )}

          {status.enabled && !recovery && (
            <form onSubmit={disable} className="mt-6 max-w-sm space-y-3">
              <div className="overline">Disable 2FA</div>
              <p className="text-xs theme-ink-muted">Confirm your password to remove the second factor.</p>
              <input type="password" required value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                data-testid="disable-2fa-pw"
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
                placeholder="Password" />
              <button type="submit" data-testid="disable-2fa-btn"
                className="border border-risk/40 text-risk px-4 py-2 text-sm rounded-sm hover:bg-risk/10">
                Disable 2FA
              </button>
            </form>
          )}
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
